import { useEffect, useMemo } from 'react'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import { raioDaCaixa, segmentosDoRaio, type Dimensoes } from './raioDaCaixa'

interface Props {
  /** Largura, altura e profundidade, como em `<boxGeometry args>`. */
  args: Dimensoes
  /** Raio da quina em metros; sem ele, acompanha a menor dimensão (ver `raioDaCaixa`). */
  raio?: number
}

/** Cria a geometria só quando uma dimensão ou o raio muda, não a cada render. */
function useGeometriaArredondada(largura: number, altura: number, profundidade: number, raio: number) {
  const geometria = useMemo(
    () => (raio === 0 ? null : new RoundedBoxGeometry(largura, altura, profundidade, segmentosDoRaio(raio), raio)),
    [largura, altura, profundidade, raio],
  )

  // O R3F não descarta o que entra por `<primitive>`: o buffer na GPU é nosso.
  useEffect(() => () => geometria?.dispose(), [geometria])

  return geometria
}

/**
 * Substituto direto de `<boxGeometry args={[l, a, p]} />` com quinas
 * arredondadas. É só a geometria: a malha, o material, as refs e o raycast
 * continuam sendo do `<mesh>` que a contém, então trocar não mexe em nada além
 * do contorno da peça.
 *
 * Quando a quina ficaria menor que `RAIO_MINIMO`, devolve a caixa reta.
 */
export function CaixaArredondada({ args, raio }: Props) {
  const raioEfetivo = raioDaCaixa(args, raio)
  const geometria = useGeometriaArredondada(args[0], args[1], args[2], raioEfetivo)

  if (geometria === null) return <boxGeometry args={[args[0], args[1], args[2]]} />
  return <primitive object={geometria} attach="geometry" />
}
